/**
 * The boundaries panel — **client review C1**, 2026-08-22. (`#layerManager`, from R8.)
 *
 * > *"The right-rail layer-manager panel now lists ONLY the five operational-hierarchy
 * > boundary rows (Countries, Regions, Communes, Watersheds, Partnerships) with checkboxes —
 * > the optional manual override for boundary display the removed 'View data by' rows used to
 * > give. Thematic layers stay only in the Layers pane."*
 *
 * ## What this panel used to be, and why it stopped being it
 *
 * R8 built it as a floating copy of the §F5.2 Layers pane: the whole thematic catalogue,
 * grouped by domain, reachable without opening the sidebar. It worked, and it was a second
 * place to do a thing the sidebar already does — two search boxes over one catalogue, two
 * copies of every row, and a reader who switched a layer on here and then went looking for its
 * opacity slider in the pane. The client's answer is that the catalogue has one home (the
 * Layers pane) and this panel gets the job nothing else does any more.
 *
 * ## The job nothing else does
 *
 * R5 deleted the §F5.8 widget's "View data by:" block — five checkbox/radio rows that were a
 * second geography control standing next to the cascade and quietly disagreeing with it. That
 * was right about the *radios* (the reporting level is a pure function of the scope) and it
 * threw away something with the checkboxes: **the manual override on which boundaries are
 * drawn.** `useHierarchyStore.setVisible` was left alive with no caller — dead code that
 * `applyScope` still wrote into — so a reader who wanted watershed outlines over an
 * unfiltered map, or the commune grid off while zoomed in, had no control anywhere. The five
 * rows come back here, off the map's own tool rail, where they are an override a reader
 * reaches for rather than a second filter standing next to the first. (Its unused sibling
 * `toggle` is gone rather than revived: a controlled checkbox reports the state it was moved
 * *to*, and a second writer that flips the stored value instead is how a box and the map it
 * draws start disagreeing.)
 *
 * **The cascade still wins.** A §F5.8 scope borrows these checkboxes on the way in
 * (`visibilityForScope`) and hands them back on the way out (`applyScope`); a row outside the
 * scope renders ticked-off, disabled, and says which filter ruled it out. That is unchanged —
 * this panel is the *manual* half of a mechanism whose automatic half already existed and was
 * already driving the map.
 *
 * **Where "which filter ruled it out" is actually said.** On the row, not on the checkbox.
 * A `title` on a `disabled` input is unreachable — Chrome does not dispatch hover events to
 * disabled form controls, so the one tooltip that explains the one control that refuses to
 * move never appears. The §F5.2 pane solved this the other way while it still had rows, and
 * solves it again in its selects: the explanation rides on the thing itself — the option text
 * — rather than on a tooltip only a live control can show. Same move here, for the same
 * reason: the row carries it, and clicking the row toasts.
 *
 * ## What is deliberately not here
 *
 * * **No thematic layers.** Not "also the layers": *only* the boundaries. The pane owns the
 *   catalogue, and a row that exists in two panels is a row that can be toggled from the panel
 *   whose opacity slider you cannot see.
 * * **No search box.** Five rows do not need one.
 * * **No reporting-level radios.** R5's deletion of those stands: the level is derived from
 *   the cascade (`levelWithinScope`), and re-offering it here would restore the exact
 *   contradiction R5 removed.
 */
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Widget } from '../../components/Widget';
import { useAppStore } from '../../lib/state';
import { toast } from '../../lib/toast';
import {
  communesUnlocked,
  countVisible,
  HIERARCHY_ROWS,
  rowInScope,
  useHierarchyStore,
} from './hierarchy';
import { useMap, useMapViewState } from './mapContext';

export interface LayerManagerProps {
  onClose: () => void;
}

export function LayerManager({ onClose }: LayerManagerProps) {
  const { t } = useTranslation();
  const map = useMap();
  const { zoom } = useMapViewState(map);
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const visible = useHierarchyStore((s) => s.visible);
  const setVisible = useHierarchyStore((s) => s.setVisible);

  // `countVisible` is the §F5.10 status bar's own definition of "drawn", borrowed rather than
  // re-derived: a ticked row whose zoom gate is shut is not on the map and must not be counted
  // here as though it were. The bar's total is larger — it adds the §F5.3 choropleth, §F5.2's
  // thematic layers and §F5.5's overlays — but the boundary term is this one, so the two can
  // never disagree about a row.
  const onCount = countVisible(visible, zoom, country, scope);

  return (
    <Widget
      id="layerManager"
      title={t('boundaries.title')}
      icon="globe"
      onClose={onClose}
      headExtra={
        <span className="lm-count" data-lmcount="">
          {t('boundaries.onCount', { count: onCount })}
        </span>
      }
    >
      <div id="lmBody">
        <div className="lm-list" data-lmlist="">
          {HIERARCHY_ROWS.map((row) => {
            const label = t(row.labelKey, row.label);
            // §F5.8's filter already unticked this row on the way in; disabling it is what
            // stops the reader putting back a boundary the filter has ruled out, which would
            // draw a country border around a single commune and call it context.
            const outOfScope = !rowInScope(row.key, scope);
            // The commune gate: ~1 050 ADM2 features programme-wide (§D2/§J1 payload budget),
            // so the row is tickable but silent until the view is local. The note says which
            // of the two ways out the reader has.
            const gated =
              Boolean(row.zoomGated) && !communesUnlocked(zoom, country, scope !== null);
            const id = `bnd-${row.key}`;
            // The short form hangs off the row as a tooltip (a hoverable element, unlike the
            // disabled box); the long form is what a click gets, because it is the one that
            // says what to *do* about it.
            const lockedTitle = t('hierarchy.filter.rowLocked', { name: scope?.name ?? '' });
            const explain = () => {
              if (outOfScope) toast(t('boundaries.rowLockedToast', { row: label, name: scope?.name ?? '' }));
            };
            return (
              <div className="bnd-row" key={row.key} data-bnd={row.key}>
                <div
                  className="bnd-main"
                  title={outOfScope ? lockedTitle : undefined}
                  {...(outOfScope
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        onClick: explain,
                        onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            explain();
                          }
                        },
                      }
                    : {})}
                >
                  <input
                    type="checkbox"
                    id={id}
                    data-ref={row.key}
                    checked={visible[row.key] && !outOfScope}
                    disabled={outOfScope}
                    // Kept even though a disabled control never shows it: it is what an
                    // accessibility tree and `LayerManager.test.tsx` read the reason off, and
                    // the row above it is what a mouse actually reaches.
                    title={outOfScope ? lockedTitle : undefined}
                    onChange={(e) => setVisible(row.key, e.target.checked)}
                  />
                  {/* The row's own line colour, so the panel and the map name the same thing:
                      a reader ticking "Watersheds" sees the blue dash they are about to get. */}
                  <span
                    className="bnd-swatch"
                    aria-hidden="true"
                    style={{
                      borderTopColor: String(row.style.color ?? 'var(--ink-3)'),
                      borderTopStyle: row.style.dashArray ? 'dashed' : 'solid',
                    }}
                  />
                  <label className="bnd-name" htmlFor={id}>
                    {label}
                    <small>{row.source}</small>
                  </label>
                </div>
                {gated && visible[row.key] && !outOfScope ? (
                  <div className="bnd-note" data-bndnote={row.key}>
                    {t('hierarchy.communesNote')}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        {/* Why the panel exists, in one line: these are an override on top of the cascade,
            not a rival to it. Standing text — it is true whether or not a filter is on. */}
        <div className="bnd-cap">{t('boundaries.caption')}</div>
      </div>
    </Widget>
  );
}

export default LayerManager;
